import { CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { consume } from '@lit/context';
import { safeDefine } from '@breuni-ui/safe-define';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { cleanupApiInstance, CleanupData, CleanupMode } from '../../../scripts/service/cleanup-api.js';
import { Messages } from '../../../@types/wishlist.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import type { BdkToggleChangeDetail } from '@breuni-ui/bdk-toggle';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';
import styles from './wishlist-cleanup-layer.scss?inline';
import '../base-layer/base-layer.js';

import('@breuni-ui/bdk-toggle').then(({ BdkToggle }) => safeDefine('bdk-ents-toggle', BdkToggle));

@customElement('ents-wishlist-cleanup-layer')
export class WishlistCleanupLayer extends LitElement {
  @property({ type: Boolean }) showLayer: boolean = false;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @state() private loading: boolean = false;
  @state() private error: boolean = false;
  @state() private purchasedChecked: boolean = false;
  @state() private notInAssortmentChecked: boolean = false;
  @state() private autoRemove: boolean = false;
  @state() private purchasedCount: number = 0;
  @state() private notInAssortmentCount: number = 0;

  private _loadedPurchased: CleanupMode = 'NONE';
  private _loadedNotInAssortment: CleanupMode = 'NONE';

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(baseLayerStyles), unsafeCSS(styles)];
  }

  protected updated(changedProperties: PropertyValues) {
    if (changedProperties.has('showLayer') && this.showLayer) {
      this.error = false;
      void this.loadPersistedState();
    }
  }

  private async loadPersistedState() {
    this.loading = true;
    try {
      const loaded = await cleanupApiInstance.getState();
      this._loadedPurchased = loaded.purchased;
      this._loadedNotInAssortment = loaded.notInAssortment;
      this.purchasedChecked = loaded.purchased !== 'NONE';
      this.notInAssortmentChecked = loaded.notInAssortment !== 'NONE';
      this.autoRemove = loaded.purchased === 'RECURRING' || loaded.notInAssortment === 'RECURRING';
      this.purchasedCount = loaded.purchasedCount ?? 0;
      this.notInAssortmentCount = loaded.notInAssortmentCount ?? 0;
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  private handleLayerClosed = () => {
    if (this.showLayer) {
      this.showLayer = false;
      this.dispatchEvent(new Event('cleanup-layer-closed', { composed: true, bubbles: true }));
    }
  };

  private get currentPurchased(): CleanupMode {
    if (!this.purchasedChecked) return 'NONE';
    return this.autoRemove ? 'RECURRING' : 'ONCE';
  }

  private get currentNotInAssortment(): CleanupMode {
    if (!this.notInAssortmentChecked) return 'NONE';
    return this.autoRemove ? 'RECURRING' : 'ONCE';
  }

  private get hasSelection(): boolean {
    return this.purchasedChecked || this.notInAssortmentChecked;
  }

  private get wouldDeleteProducts(): boolean {
    return (
      (this.purchasedChecked && this.purchasedCount > 0) ||
      (this.notInAssortmentChecked && this.notInAssortmentCount > 0)
    );
  }

  private get isDirty(): boolean {
    return (
      this.currentPurchased !== this._loadedPurchased || this.currentNotInAssortment !== this._loadedNotInAssortment
    );
  }

  private handlePurchasedChange(e: Event) {
    this.purchasedChecked = (e.target as HTMLInputElement).checked;
    if (!this.hasSelection) {
      this.autoRemove = false;
    }
  }

  private handleNotInAssortmentChange(e: Event) {
    this.notInAssortmentChecked = (e.target as HTMLInputElement).checked;
    if (!this.hasSelection) {
      this.autoRemove = false;
    }
  }

  private handleToggleChange(e: CustomEvent<BdkToggleChangeDetail>) {
    this.autoRemove = e.detail.checked;
  }

  private handleToggleLabelClick() {
    if (this.hasSelection) {
      this.autoRemove = !this.autoRemove;
    }
  }

  private async handleConfirm() {
    this.loading = true;
    try {
      const data: CleanupData = {
        purchased: this.currentPurchased,
        notInAssortment: this.currentNotInAssortment,
      };

      const willDelete = this.wouldDeleteProducts;
      const success = await cleanupApiInstance.cleanup(data);
      if (success) {
        if (willDelete) {
          window.location.reload();
        } else {
          this.handleLayerClosed();
        }
      } else {
        this.error = true;
      }
    } finally {
      this.loading = false;
    }
  }

  private renderContent() {
    const isToggleDisabled = !this.hasSelection;

    return html`
      ${this.error
        ? html`<p class="ents-copy ents-cleanup__error" data-test-ents-cleanup-error>
            ${this.messages['ents_wishlist_cleanup_error']}
          </p>`
        : nothing}
      <div class="ents-cleanup">
        <p class="ents-copy ents-cleanup__description">${this.messages['ents_wishlist_cleanup_description']}</p>
        <div class="ents-cleanup__options">
          <label class="bdk-checkbox ents-cleanup__checkbox-row" data-test-ents-cleanup-purchased>
            <input
              class="bdk-checkbox-input"
              type="checkbox"
              .checked="${this.purchasedChecked}"
              @change="${this.handlePurchasedChange}"
              data-test-ents-cleanup-purchased-checkbox
            />
            <span class="bdk-checkbox-text ents-cleanup__checkbox-label"
              >${this.messages['ents_wishlist_cleanup_purchased_label']}</span
            >
            <span class="ents-cleanup__badge">${this.purchasedCount}</span>
          </label>
          <label class="bdk-checkbox ents-cleanup__checkbox-row" data-test-ents-cleanup-not-in-assortment>
            <input
              class="bdk-checkbox-input"
              type="checkbox"
              .checked="${this.notInAssortmentChecked}"
              @change="${this.handleNotInAssortmentChange}"
              data-test-ents-cleanup-not-in-assortment-checkbox
            />
            <span class="bdk-checkbox-text ents-cleanup__checkbox-label"
              >${this.messages['ents_wishlist_cleanup_not_in_assortment_label']}</span
            >
            <span class="ents-cleanup__badge">${this.notInAssortmentCount}</span>
          </label>
          <div class="ents-cleanup__toggle-section">
            <div
              class=${classMap({
                'ents-cleanup__toggle-labels': true,
                'ents-cleanup__toggle-labels--enabled': !isToggleDisabled,
              })}
              @click="${this.handleToggleLabelClick}"
            >
              <span class="ents-cleanup__toggle-title"
                >${this.messages['ents_wishlist_cleanup_auto_remove_title']}</span
              >
              <span class="ents-cleanup__toggle-description">
                ${this.messages['ents_wishlist_cleanup_auto_remove_description']}
              </span>
            </div>
            <bdk-ents-toggle
              .checked="${this.autoRemove}"
              .disabled="${isToggleDisabled}"
              aria-label="${this.messages['ents_wishlist_cleanup_auto_remove_title']}"
              data-test-ents-cleanup-toggle
              @bdk-toggle-change="${this.handleToggleChange}"
            ></bdk-ents-toggle>
          </div>
        </div>
      </div>
    `;
  }

  protected render() {
    const isButtonDisabled = this.loading || !this.isDirty;

    return html`
      <ents-base-layer
        drawer-test-id="ents-wishlist-cleanup-layer"
        ?showLayer="${this.showLayer}"
        @ents-layer-closed="${this.handleLayerClosed}"
      >
        <h3 class="ents-heading-medium" slot="title">${this.messages['ents_wishlist_cleanup_title']}</h3>
        ${this.loading
          ? html`<div class="ents-cleanup__loading" aria-busy="true">
              <span class="ents-cleanup__spinner" aria-hidden="true"></span>
            </div>`
          : this.renderContent()}
        <div slot="footer" class="ents-cleanup__footer">
          <button
            class="bdk-button"
            type="button"
            variant="primary"
            size="medium"
            full-width
            ?disabled="${isButtonDisabled}"
            @click="${this.handleConfirm}"
            data-test-ents-wishlist-cleanup-confirm
          >
            ${this.messages['ents_wishlist_cleanup_confirm']}
          </button>
        </div>
      </ents-base-layer>
    `;
  }
}
